/**
 * Overlap detection for cancellation bands (DNKADM-0057 U2).
 *
 * The server enforces a unique `(scope, bucket, minHoursBeforeStart)` per band, but the
 * UI **warns** rather than blocks on a duplicate (it never blocks submit) — matching the
 * contract's "largest qualifying" evaluation, where a duplicate is a configuration
 * smell, not an error. The table flags any band whose `(bucket, minHoursBeforeStart)`
 * scope appears more than once; the dialog flags inline when the in-progress values
 * collide with an existing OTHER band.
 *
 * `creditBucketId: null` ("All buckets", `scope=ALL`) is its own distinct scope — a
 * null-bucket band and a named-bucket band at the same hours do NOT overlap (they
 * target different scopes).
 */
import type { CancellationBand } from '@/api/policies';

/**
 * Stable key for a band's `(bucket, minHoursBeforeStart)` scope. `null` bucket is its
 * own distinct scope (tagged `ALL`), and a named bucket is tagged `B:` so a bucket id
 * literally equal to "ALL" can't collide with the all-buckets scope. The hours are
 * joined with a `#` separator that cannot appear in a bucket id.
 */
export function bandKey(creditBucketId: string | null, minHoursBeforeStart: number): string {
  const scope = creditBucketId === null ? 'ALL' : `B:${creditBucketId}`;
  return `${scope}#${minHoursBeforeStart}`;
}

/**
 * The set of `(bucket, minHoursBeforeStart)` keys that appear on MORE than one band —
 * i.e. every overlapping pair. Used by the table to flag each colliding row.
 */
export function overlappingKeys(bands: readonly CancellationBand[]): Set<string> {
  const counts = new Map<string, number>();
  for (const b of bands) {
    const key = bandKey(b.creditBucketId, b.minHoursBeforeStart);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  const dupes = new Set<string>();
  for (const [key, count] of counts) {
    if (count > 1) dupes.add(key);
  }
  return dupes;
}

/**
 * True when a band with `(bucket, minHoursBeforeStart)` would collide with an existing
 * band OTHER than `excludeId` (the band being edited). Used by the dialog for the
 * inline overlap warning — it warns, it does not block submit.
 */
export function collidesWithExisting(
  bands: readonly CancellationBand[],
  creditBucketId: string | null,
  minHoursBeforeStart: number,
  excludeId: string | null,
): boolean {
  const key = bandKey(creditBucketId, minHoursBeforeStart);
  return bands.some(
    (b) =>
      (excludeId === null || b.id !== excludeId) &&
      bandKey(b.creditBucketId, b.minHoursBeforeStart) === key,
  );
}
